'use client'
import { CodeBlockTabs } from 'fumadocs-ui/components/codeblock'
import { Children, isValidElement, useEffect, useRef, useState, type ComponentProps } from 'react'

const KEY = 'ad-code-lang'
const EVENT = 'ad-code-lang'

/**
 * Code tabs (```js tab="JavaScript") that remember the reader's language: picking Python in one
 * block switches every block on the page that has Python, and later pages open on it too. A
 * block without that language keeps its own tab, where Fumadocs' groupId would leave it blank.
 */
export function SyncedCodeBlockTabs({ defaultValue, children, ...props }: ComponentProps<typeof CodeBlockTabs>) {
  const ref = useRef<HTMLDivElement>(null)
  const values = Children.toArray(children).flatMap((child) =>
    isValidElement<{ value?: unknown }>(child) && typeof child.props.value === 'string' ? [child.props.value] : [],
  )
  // `children` is a new array on every render; the joined values only change when the tabs do
  const known = values.join('\n')
  // A block written by hand without defaultValue still opens on its first tab
  const [value, setValue] = useState(defaultValue ?? values[0])

  useEffect(() => {
    const pick = (v: string | null) => {
      if (v && known.split('\n').includes(v)) setValue(v)
    }
    try {
      pick(localStorage.getItem(KEY))
    } catch {}
    const onPick = (e: Event) => pick((e as CustomEvent<string>).detail)
    window.addEventListener(EVENT, onPick)
    return () => window.removeEventListener(EVENT, onPick)
  }, [known])

  return (
    <CodeBlockTabs
      ref={ref}
      {...props}
      value={value}
      onValueChange={(v) => {
        // Blocks above can change height as they switch; keep the one clicked where it was
        const top = ref.current?.getBoundingClientRect().top
        setValue(v)
        try {
          localStorage.setItem(KEY, v)
        } catch {}
        window.dispatchEvent(new CustomEvent(EVENT, { detail: v }))
        requestAnimationFrame(() => {
          const now = ref.current?.getBoundingClientRect().top
          if (top !== undefined && now !== undefined && now !== top) window.scrollBy(0, now - top)
        })
      }}>
      {children}
    </CodeBlockTabs>
  )
}
